// composables/v3/useChunkError.ts — state and detection behind the
// connection-error screen.
//
// Nuxt splits the app into chunks that load on demand. When one fails to load
// the guest is stuck: the route component never arrives and nothing renders.
// Two things cause it in production:
//
//   1. the guest's connection dropped mid-request (62% of these events come
//      from the LINE in-app browser, where backgrounding the app kills
//      in-flight requests)
//   2. a deploy replaced the chunk: filenames are content hashes, so a new
//      build deletes the old names while a cached `index.html` still asks for
//      them
//
// `useState` rather than a Pinia store: the screen is rendered from `app.vue`
// and `error.vue`, above every layout, and must not depend on plugin ordering.
import { useState } from '#imports'

export const CHUNK_ERROR_KEY = 'v3:chunk-error'

/** True once a chunk has failed to load; reset only by reloading the page. */
export const useChunkError = () => useState<boolean>(CHUNK_ERROR_KEY, () => false)

/**
 * Wording differs per engine AND per the path the failure takes out of the app,
 * so this matches text rather than a type:
 *
 *   - tapping a button    -> `navigateTo()` rejects inside the click handler and
 *                            Vue reports it; the router never sees it
 *   - the first resolve   -> vue-router's "Couldn't resolve component"
 *   - a router navigation -> Nuxt's own `app:chunkError`
 */
export const CHUNK_ERROR_PATTERNS = [
  'dynamically imported module',      // Chrome / Android WebView, Firefox
  'importing a module script failed', // Safari / iOS
  "couldn't resolve component",       // vue-router, first resolve
  "reading 'default'",                // the unwrapped import
  'failed to fetch',
]

/** True when `e` looks like a chunk that failed to load. */
export function isChunkError(e: unknown): boolean {
  const message = (
    e instanceof Error ? e.message : typeof e === 'string' ? e : String((e as { message?: string })?.message ?? '')
  ).toLowerCase()
  if (!message) return false
  return CHUNK_ERROR_PATTERNS.some((p) => message.includes(p))
}
